import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { useFormik } from 'formik';
import NepaliInputBox from '../../../../constants/NepaliInputBox';
import { handleNepaliInputChange } from '../../../../helpers/handleChange';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import { putDataToTheServer } from '../../../../helpers/api';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import { ProductStore, ProductUnit } from '../../../../helpers/models/Models';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { YesNoOptions } from '../../../../constants/Options';

interface ItemStoreListViewProps {
    results: {
        results: ProductStore[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const ItemStoreListView: React.FC<ItemStoreListViewProps> = ({ results, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<ProductStore | null>(null);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: false },
        { header: 'Store Name', accessor: 'store_name', sortable: true },
        { header: 'Store Name(Nepali)', accessor: 'store_name_nep', sortable: false },
        { header: 'Store Code', accessor: 'store_code', sortable: false },
        { header: 'Address', accessor: 'address', sortable: false },
        { header: 'Address(Nepali)', accessor: 'address_nep', sortable: false },
        { header: 'Phone No.', accessor: 'phone_no', sortable: false },
        { header: 'E-mail', accessor: 'email', sortable: false },
        { header: 'Lists', accessor: 'list', sortable: false },
        { header: 'Actions', accessor: 'actions', action: true },
    ];
    const modifiedData = useMemo(() => {
        if (!results) return [];
        return results.results.map((item: ProductStore) => ({
            sn: item.sn,
            store_name: item.store_name,
            store_name_nep: item.store_name_nep,
            store_code: item.store_code,
            status: item.status,
            active: item.status,
            address: item.address,
            address_nep: item.address_nep,
            phone_no: item.phone_no,
            email: item.email,
            list: item.list,
            branch: item.branch,
            remarks: item.remarks
        }));
    }, [results]);

    const handleEdit = (item: ProductStore | null) => {
        setCurrentItem(item); // Use logical OR to default to an empty array if item is null or undefined
        setDialogOpen(true);
    };



    const formik = useFormik({
        initialValues: {
            store_name: currentItem?.store_name || "",
            store_name_nep: currentItem?.store_name_nep || "",
            store_code: currentItem?.store_code || "",
            email: currentItem?.email || "",
            phone_no: currentItem?.phone_no || "",
            address: currentItem?.address || "",
            address_nep: currentItem?.address_nep || "",
            status: currentItem?.status === 1 ? "1" : "2" || "",
            list: currentItem?.list || "",
            remarks: currentItem?.remarks || "",
        },
        // validationSchema: readerEditValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            Object.keys(values).forEach((key) => {
                const value = (values as Record<string, any>)[key];
                if (key === 'status') {
                    formData.append("status", value === "2" ? "0" : "1");
                } else if (value !== undefined && value !== null && value !== "") {
                    formData.append(key, value);
                }
            });
            try {
                await putDataToTheServer(AppURL.InventoryURL + 'inventory/product/item/store/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    }
                    else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => results?.next && setOnURL(results.next)}
                onPrev={() => results?.previous && setOnURL(results.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Product Store Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='store_name'
                                    label='Store Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.store_name}
                                    error={typeof formik.errors.store_name === 'string' ? formik.errors.store_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.store_name}
                                    required={true}
                                    placeholder='Enter Store Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Store Name(Nepali)'
                                    name='store_name_nep'
                                    value={formik.values.store_name_nep}
                                    handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'store_name_nep' })}
                                    required={true}
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.store_name_nep === 'string' ? formik.errors.store_name_nep : undefined}
                                    placeholder='Enter Store Name(Nepali)'
                                    touched={formik.touched.store_name_nep}
                                />
                            </Grid>

                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='store_code'
                                    label='Store Code'
                                    handleChange={formik.handleChange}
                                    value={formik.values.store_code}
                                    error={typeof formik.errors.store_code === 'string' ? formik.errors.store_code : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.store_code}
                                    required={true}
                                    placeholder='Enter Store Code'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='phone_no'
                                    label='Phone No.'
                                    handleChange={formik.handleChange}
                                    value={formik.values.phone_no}
                                    error={typeof formik.errors.phone_no === 'string' ? formik.errors.phone_no : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.phone_no}
                                    required={true}
                                    placeholder='Enter Phone No.'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='address'
                                    label='Address'
                                    handleChange={formik.handleChange}
                                    value={formik.values.address}
                                    error={typeof formik.errors.address === 'string' ? formik.errors.address : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.address}
                                    required={true}
                                    placeholder='Enter Address'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Address(Nepali)'
                                    name='address_nep'
                                    value={formik.values.address_nep}
                                    handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'address_nep' })}
                                    required={true}
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.address_nep === 'string' ? formik.errors.address_nep : undefined}
                                    placeholder='Enter Address (Nepali)'
                                    touched={formik.touched.address_nep}
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='email'
                                    name='email'
                                    label='E-mail'
                                    handleChange={formik.handleChange}
                                    value={formik.values.email}
                                    error={typeof formik.errors.email === 'string' ? formik.errors.email : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.email}
                                    required={true}
                                    placeholder='Enter E-mail'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='list'
                                    label='List'
                                    handleChange={formik.handleChange}
                                    value={formik.values.list}
                                    error={typeof formik.errors.list === 'string' ? formik.errors.list : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.list}
                                    required={true}
                                    placeholder='Enter list'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='remarks'
                                    label='Remarks'
                                    handleChange={formik.handleChange}
                                    value={formik.values.remarks}
                                    error={typeof formik.errors.remarks === 'string' ? formik.errors.remarks : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.remarks}
                                    required={false}
                                    placeholder='Enter Remarks'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="status"
                                    label='Active'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.status}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    clear={true}
                                    placeholder="Select Options"
                                />
                            </Grid>

                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default ItemStoreListView;
